import { useEffect, useState, RefObject } from "react";
import { core } from "@servicetitan/hammer-token";

/**
 * Return type for the useContainerQuery hook
 */
export type ContainerQueryReturnProps = {
  /**
   * The breakpoint name
   */
  name: "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
  /**
   * Minimum width for this breakpoint
   */
  min: number | undefined;
  /**
   * Maximum width for this breakpoint
   */
  max: number | undefined;
  /**
   * Current container width
   */
  containerWidth: number;
  /**
   * Current container height
   */
  containerHeight: number;
};

/**
 * Determines if the current container width matches a specific breakpoint
 * @param containerWidth - The container width
 * @param name - The breakpoint name
 * @param min - Minimum width for this breakpoint
 * @param max - Maximum width for this breakpoint
 * @returns Breakpoint information if matched, undefined otherwise
 */
function containerBreakpoint(
  containerWidth: number,
  name: ContainerQueryReturnProps["name"],
  min?: number,
  max?: number,
): ContainerQueryReturnProps | undefined {
  if (
    (min == null || containerWidth >= min) &&
    (max == null || containerWidth < max)
  ) {
    return {
      name,
      min,
      max,
      containerWidth,
      containerHeight: 0, // Will be updated with actual height
    } as const;
  }
  return undefined;
}

/**
 * Determines the current breakpoint based on container width and hammer-token breakpoint values.
 *
 * Features:
 * - Validates that all required hammer-token breakpoint values are available
 * - Uses cascade logic to determine the appropriate breakpoint
 * - Returns the first matching breakpoint in order of preference
 * - Handles edge cases where breakpoint values are missing
 * - Uses container dimensions for accurate measurements
 * - Supports all standard breakpoints (xs, sm, md, lg, xl, xxl)
 *
 * @param containerWidth - The width of the container element
 * @param containerHeight - The height of the container element
 * @returns Current breakpoint information or undefined if breakpoint values are unavailable
 */
const getContainerBreakpoint = (
  containerWidth: number,
  containerHeight: number,
) => {
  if (!core.primitive?.BreakpointSm) return;
  if (!core.primitive?.BreakpointMd) return;
  if (!core.primitive?.BreakpointLg) return;
  if (!core.primitive?.BreakpointXl) return;
  if (!core.primitive?.BreakpointXxl) return;

  const sm = parseInt(core.primitive?.BreakpointSm.value);
  const md = parseInt(core.primitive?.BreakpointMd.value);
  const lg = parseInt(core.primitive?.BreakpointLg.value);
  const xl = parseInt(core.primitive?.BreakpointXl.value);
  const xxl = parseInt(core.primitive?.BreakpointXxl.value);

  const result =
    containerBreakpoint(containerWidth, "xs", undefined, sm) ??
    containerBreakpoint(containerWidth, "sm", sm, md) ??
    containerBreakpoint(containerWidth, "md", md, lg) ??
    containerBreakpoint(containerWidth, "lg", lg, xl) ??
    containerBreakpoint(containerWidth, "xl", xl, xxl) ??
    containerBreakpoint(containerWidth, "xxl", xxl, undefined);

  return result ? { ...result, containerHeight } : undefined;
};

/**
 * Custom hook for detecting current breakpoint based on container dimensions.
 *
 * Features:
 * - Detects current breakpoint based on container width instead of viewport width
 * - Supports all standard breakpoints (xs, sm, md, lg, xl, xxl)
 * - Provides container dimensions information
 * - Uses hammer-token breakpoint values for consistency
 * - Listens for container resize events using ResizeObserver
 * - Supports optional disable functionality
 * - Uses container element dimensions for accurate measurements
 *
 * @param containerRef - React ref to the container element to observe
 * @param props - Optional configuration object
 * @param props.disable - Whether to disable the hook
 * @returns Current breakpoint information or undefined if disabled
 */
export const useContainerQuery = (
  containerRef: RefObject<HTMLElement>,
  props?: { disable?: boolean },
) => {
  const [size, setSize] = useState<ContainerQueryReturnProps | undefined>(
    undefined,
  );

  useEffect(() => {
    if (props?.disable || !containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        const breakpoint = getContainerBreakpoint(width, height);
        setSize(breakpoint);
      }
    });

    resizeObserver.observe(containerRef.current);

    // Set initial size
    const rect = containerRef.current.getBoundingClientRect();
    setSize(getContainerBreakpoint(rect.width, rect.height));

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, props?.disable]);

  if (props?.disable) return undefined;
  return size;
};
